import { LinkButton } from "@/components/LinkButton";
import { setFollowUpProjectContext } from "@/features/appointments/followUpProjectContext";
import { isOpenSlot } from "@/features/appointments/utils";
import { useModals } from "@/features/modals/hooks";
import { useCalendarSettings } from "@repo/api";
import { type Appointment, type Customer } from "@repo/api";
import {
  Button,
  arrowLeftArrowRightIcon,
  briefcaseIcon,
  crossIcon,
  linkOutIcon,
  personIcon,
  warningTriangleIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import { ElementsContainer, openNewProjectModal, useSettings } from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import { startOfDay } from "date-fns";
import { useNavigate } from "react-router-dom";

import { useAppointmentActions } from "../../useAppointmentActions";
import {
  type AppointmentModalState,
  type AppointmentModalStateActions,
} from "./AppointmentModalReducer";

interface Props {
  state: AppointmentModalState;
  actions: AppointmentModalStateActions;
  onClose: () => void;
  mutateSearchAppointments?: () => void;
  customer?: Customer;
  onBookFollowUp?: (appointment: Appointment, customer?: Customer) => void;
}

export const AppointmentMoreActionButtons = ({
  state,
  actions,
  onClose,
  mutateSearchAppointments,
  customer,
  onBookFollowUp,
}: Props) => {
  const navigate = useNavigate();
  const { boka_config } = useSettings();
  const { pushModal } = useModals();
  const { data: calendarSettings } = useCalendarSettings();
  const {
    openLink,
    disableCancel,
    disableNoShow,
    disableAssign,
    disableMove,
    onSelectProject,
    onProjectCreated,
    onAssign,
    onCancel,
  } = useAppointmentActions(state, actions, onClose, mutateSearchAppointments);

  if (!state.appointment) {
    return null;
  }

  const showBookFollowUpAction =
    Boolean(boka_config?.enable_boka_follow_up_integration) &&
    state.appointment.type === "BOKA_APPOINTMENT" &&
    !isOpenSlot(
      state.appointment.reasonCode,
      state.appointment.orderNumber,
      state.appointment.type
    ) &&
    !state.appointment.followupOrderNumber &&
    (state.context === "CALENDAR"
      ? onBookFollowUp !== undefined
      : state.opportunityId !== undefined);

  const handleBookFollowUp = () => {
    if (!state.appointment) {
      return;
    }

    if (state.context === "CALENDAR" && onBookFollowUp) {
      trackAnalytics("book_follow_up");
      onBookFollowUp(state.appointment, customer);
      onClose();
      return;
    }

    if (!state.opportunityId) {
      return;
    }

    setFollowUpProjectContext({
      projectId: state.opportunityId,
      appointmentId: state.appointment.appointmentId,
    });

    const selectedTeamId =
      calendarSettings?.selectedTeam ?? state.appointment.assignedTeam;
    const appointmentStartTime = state.appointment.timeWindowStartTime;
    const targetDate = appointmentStartTime
      ? startOfDay(new Date(appointmentStartTime))
      : startOfDay(new Date());
    const params = new URLSearchParams({
      tableView: "TEAM",
      selectedDate: String(targetDate.getTime()),
    });

    if (selectedTeamId) {
      params.set("teamId", String(selectedTeamId));
    }

    trackAnalytics("book_follow_up");
    onClose();
    navigate(`/calendar?${params.toString()}`);
  };

  return (
    <ElementsContainer>
      {state.context === "CALENDAR" &&
        state.appointment.opportunityId !== undefined && (
          <LinkButton
            ssrIcon={briefcaseIcon}
            to={`/opportunity/${state.opportunityId}`}
            type="secondary"
            text={t("action.open_project")}
            onClick={() => {
              trackAnalytics("open_project_page");
            }}
          />
        )}

      {showBookFollowUpAction && (
        <Button
          text={t("action.book_follow_up_appointment")}
          onClick={handleBookFollowUp}
        />
      )}

      <Button
        ssrIcon={personIcon}
        text={
          state.appointment.assignedCoworker
            ? t("action.reassign")
            : t("action.assign")
        }
        onClick={() => {
          if (state.appointment?.type === "DROP_IN") {
            actions.setStep("EDIT");
          } else {
            onAssign();
          }
        }}
        disabled={disableAssign}
        loading={state.loading === "ASSIGNING"}
      />

      <Button
        ssrIcon={crossIcon}
        text={t("action.cancel_appointment")}
        disabled={disableCancel || state.loading === "CANCELLING"}
        loading={state.loading === "CANCELLING"}
        onClick={() => onCancel()}
      />

      <Button
        ssrIcon={warningTriangleIcon}
        text={t("label.no_show")}
        disabled={disableNoShow || state.loading === "NO_SHOW"}
        loading={state.loading === "NO_SHOW"}
        onClick={() => onCancel("NO_SHOW")}
      />

      <Button
        ssrIcon={arrowLeftArrowRightIcon}
        text={t("action.move_to_new_project")}
        onClick={() => {
          openNewProjectModal({
            pushModal,
            resetModal: true,
            onSelectProject,
            onProjectCreated,
            customerInfo: customer,
            departments: state.departments,
            filterCurrentProject: state.appointment?.opportunityId,
          });
        }}
        disabled={disableMove}
      />
      <Button
        ssrIcon={linkOutIcon}
        text={t("action.open_in_boka")}
        disabled={!state.appointment?.appointmentLink}
        onClick={() => {
          openLink();
          trackAnalytics("open_in_boka");
        }}
      />
    </ElementsContainer>
  );
};
